import {
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
  Optional,
  Self,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { PlIcon } from '@app/icon-library/icons';
import { RatingValue } from '@shared/models/step.model';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NgControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface FeedbackValue {
  ratingValue: RatingValue | null;
  feedbackText: string | null;
}

enum FormControlName {
  rating = 'rating',
  feedbackText = 'feedbackText',
}

interface FeedbackForm {
  [FormControlName.rating]: FormControl<number>;
  [FormControlName.feedbackText]: FormControl<string>;
}

@Component({
  selector: 'pl-feedback',
  templateUrl: './feedback.component.html',
})
export class FeedbackComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) public title!: string;
  @Input({ required: true }) public labelFeedbackText!: string;
  @Input({ required: true }) public placeholderFeedbackText!: string;
  @Input() public isFormSubmitted: boolean = false;
  @Input() public isDisabled: boolean = false;

  public readonly STARS_NUMBER: number = 5;
  public stars: number[] = this.getStarsArrayNonSelected();
  public indexHoveredStar: WritableSignal<number> = signal(-1);

  public feedbackForm!: FormGroup<FeedbackForm>;

  protected readonly FormControlName: typeof FormControlName = FormControlName;
  protected readonly PlIcon: typeof PlIcon = PlIcon;

  private onChangeCallback!: (value: FeedbackValue) => void;
  private onTouchedCallback!: () => void;

  @ViewChild('plTextArea', { read: ElementRef }) public plTextArea!: ElementRef;

  @HostBinding('tabindex') public tabindex: number = 1;
  @HostListener('focus') public onFocus(): void {
    console.log('TONIO FeedbackComponent onFocus this.plTextArea=', this.plTextArea);
    // preventScroll: true because scroll is handled by directive plScrollToInvalidControlOnSubmit
    if (this.plTextArea) {
      this.plTextArea.nativeElement.focus({ preventScroll: true });
    }
  }

  protected destroyRef: DestroyRef = inject(DestroyRef);

  constructor(@Optional() @Self() public controlDir: NgControl) {
    this.createForm();

    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  private createForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.feedbackForm = fb.group<FeedbackForm>({
      [FormControlName.rating]: fb.nonNullable.control<number>(-1, []),
      [FormControlName.feedbackText]: fb.nonNullable.control<string>('', []),
    });
  }

  public ngOnInit(): void {
    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;
      if (control) {
        control.setValidators([this.validate.bind(this, control?.hasValidator(Validators.required))]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }

    this.watchFeedbackChanges();
  }

  public watchFeedbackChanges(): void {
    this.feedbackForm.controls[FormControlName.feedbackText].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.emitChanges();
      });
  }

  private validate(isRequired: boolean): ValidationErrors | null {
    let allErrors: any = {};
    if (isRequired) {
      const ratingValue: number = this.feedbackForm.controls[FormControlName.rating].value;
      if (ratingValue === -1) {
        allErrors.required = true;
      }
    }

    if (this.feedbackForm.controls[FormControlName.feedbackText].errors) {
      allErrors = {
        ...allErrors,
        ...this.feedbackForm.controls[FormControlName.feedbackText].errors,
      };
    }

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public registerOnChange(fn: (value: FeedbackValue) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public writeValue(valueToWrite: FeedbackValue | undefined): void {
    if (valueToWrite !== undefined) {
      this.feedbackForm.controls[FormControlName.rating].setValue(
        this.convertRatingValueToNumber(valueToWrite.ratingValue),
      );
      this.feedbackForm.controls[FormControlName.feedbackText].setValue(valueToWrite.feedbackText || '');
    }
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  private emitChanges(): void {
    const feedbackText: string = this.feedbackForm.controls[FormControlName.feedbackText].value;
    const feedbackValue: FeedbackValue = {
      ratingValue: this.convertNumberToRatingValue(this.feedbackForm.controls[FormControlName.rating].value),
      feedbackText: feedbackText ? feedbackText : null,
    };
    this.onChangeCallback(feedbackValue);
  }

  public selectStar(indexStarToSelect: number): void {
    this.onTouched();
    this.feedbackForm.controls[FormControlName.rating].setValue(indexStarToSelect);
    this.emitChanges();
  }

  private convertNumberToRatingValue(indexStarToSelect: number): RatingValue | null {
    if (indexStarToSelect === -1) {
      return null;
    } else if (indexStarToSelect >= 0 || indexStarToSelect < this.STARS_NUMBER) {
      return `${indexStarToSelect}` as RatingValue;
    }

    throw new Error(`Invalid value for indexStarToSelect: ${indexStarToSelect}`);
  }

  private convertRatingValueToNumber(ratingValue: RatingValue | null): number {
    if (ratingValue === null) {
      return -1;
    } else {
      return +ratingValue;
    }
  }

  public setIndexMouseHovered(indexStarHovered: number): void {
    this.indexHoveredStar.set(indexStarHovered);
  }

  private getStarsArrayNonSelected(): number[] {
    return [...Array(this.STARS_NUMBER).keys()];
  }
}
